import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import configuracionApi from '../api/configuracionApi';

const ConfigEmpresaContext = createContext();

/**
 * Context para manejar la configuración de la empresa
 * Cachea la configuración y proporciona helpers para validaciones
 */
export const ConfigEmpresaProvider = ({ children }) => {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Cargar configuración de la empresa desde el backend
   */
  const cargarConfiguracion = useCallback(async () => {
    if (!user) {
      setConfig(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await configuracionApi.getConfiguracion();
      setConfig(response.data);
      setError(null);
    } catch (err) {
      console.error('Error cargando configuración de empresa:', err);
      setError(err.message || 'Error al cargar configuración');
      setConfig(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Cargar configuración al iniciar o cuando cambie el usuario
  useEffect(() => {
    cargarConfiguracion();
  }, [cargarConfiguracion]);

  // ========================================
  // HELPERS DE VALIDACIÓN Y UTILIDADES
  // ========================================

  /**
   * Verificar si la empresa tiene GIS activado
   * @returns {boolean}
   */
  const tieneGIS = useCallback(() => {
    return config?.soporte_gis === true;
  }, [config]);

  /**
   * Verificar si un estado está en los estados activos de la empresa
   * @param {number} idEstado - ID del estado a verificar
   * @returns {boolean}
   */
  const esEstadoActivo = useCallback((idEstado) => {
    if (!config?.estados_activos) return false;
    return config.estados_activos.includes(parseInt(idEstado));
  }, [config]);

  /**
   * Verificar si un estado es especial (estados del sistema)
   * @param {number} idEstado - ID del estado a verificar
   * @returns {boolean}
   */
  const esEstadoEspecial = useCallback((idEstado) => {
    if (!config?.estados_especiales) return false;
    return config.estados_especiales.includes(parseInt(idEstado));
  }, [config]);

  /**
   * Obtener el nombre personalizado de un estado
   * @param {number} idEstado - ID del estado
   * @returns {string|null} Nombre custom o null si usa el nombre por defecto
   */
  const obtenerNombreEstado = useCallback((idEstado) => {
    if (config?.nombres_estados_custom && config.nombres_estados_custom[idEstado]) {
      return config.nombres_estados_custom[idEstado];
    }
    return null; // Usar nombre por defecto
  }, [config]);

  /**
   * Obtener array de IDs de estados activos
   * @returns {Array<number>}
   */
  const getEstadosActivos = useCallback(() => {
    return config?.estados_activos || [];
  }, [config]);

  /**
   * Obtener objeto completo de nombres custom de estados
   * @returns {Object}
   */
  const getNombresCustom = useCallback(() => {
    return config?.nombres_estados_custom || {};
  }, [config]);

  /**
   * Verificar si un estado está permitido (activo o especial)
   * @param {number} idEstado - ID del estado
   * @returns {boolean}
   */
  const esEstadoPermitido = useCallback((idEstado) => {
    return esEstadoActivo(idEstado) || esEstadoEspecial(idEstado);
  }, [esEstadoActivo, esEstadoEspecial]);

  // ========================================
  // ACCIONES DE CONFIGURACIÓN
  // ========================================

  /**
   * Aplicar una plantilla predefinida a la empresa
   * @param {number} idPlantilla - ID de la plantilla
   * @returns {Promise<Object>} { success: boolean, error?: string }
   */
  const aplicarPlantilla = async (idPlantilla) => {
    try {
      await configuracionApi.aplicarPlantilla(idPlantilla);
      await cargarConfiguracion(); // Recargar config actualizada
      return { success: true };
    } catch (err) {
      console.error('Error aplicando plantilla:', err);
      return {
        success: false,
        error: err.response?.data?.error || err.message || 'Error al aplicar plantilla'
      };
    }
  };

  /**
   * Actualizar configuración general de la empresa
   * @param {Object} configData - Datos de configuración a actualizar
   * @returns {Promise<Object>} { success: boolean, error?: string }
   */
  const actualizarConfiguracion = async (configData) => {
    try {
      await configuracionApi.updateConfiguracion(configData);
      await cargarConfiguracion(); // Recargar config actualizada
      return { success: true };
    } catch (err) {
      console.error('Error actualizando configuración:', err);
      return {
        success: false,
        error: err.response?.data?.error || err.message || 'Error al actualizar configuración'
      };
    }
  };

  /**
   * Personalizar nombres de estados de la empresa
   * @param {Object} nombresCustom - Objeto con nombres personalizados { id_estado: nombre }
   * @returns {Promise<Object>} { success: boolean, error?: string }
   */
  const personalizarNombresEstados = async (nombresCustom) => {
    try {
      await configuracionApi.personalizarEstados(nombresCustom);
      await cargarConfiguracion();
      return { success: true };
    } catch (err) {
      console.error('Error personalizando estados:', err);
      return {
        success: false,
        error: err.response?.data?.error || err.message || 'Error al personalizar estados'
      };
    }
  };

  /**
   * Configurar soporte GIS para la empresa
   * @param {boolean} activar - true para activar, false para desactivar
   * @returns {Promise<Object>} { success: boolean, error?: string }
   */
  const configurarGIS = async (activar) => {
    try {
      await configuracionApi.configurarGIS(activar);
      await cargarConfiguracion();
      return { success: true };
    } catch (err) {
      console.error('Error configurando GIS:', err);
      return {
        success: false,
        error: err.response?.data?.error || err.message || 'Error al configurar GIS'
      };
    }
  };

  /**
   * Obtener historial de cambios de configuración
   * @returns {Promise<Array>}
   */
  const obtenerHistorial = async () => {
    try {
      const response = await configuracionApi.getHistorial();
      return response.data || [];
    } catch (err) {
      console.error('Error obteniendo historial:', err);
      return [];
    }
  };

  const value = {
    // Estado
    config,
    loading,
    error,

    // Función principal
    cargarConfiguracion,

    // Helpers de validación
    tieneGIS,
    esEstadoActivo,
    esEstadoEspecial,
    esEstadoPermitido,
    obtenerNombreEstado,
    getEstadosActivos,
    getNombresCustom,

    // Acciones
    aplicarPlantilla,
    actualizarConfiguracion,
    personalizarNombresEstados,
    configurarGIS,
    obtenerHistorial
  };

  return (
    <ConfigEmpresaContext.Provider value={value}>
      {children}
    </ConfigEmpresaContext.Provider>
  );
};

/**
 * Hook para usar el contexto de configuración de empresa
 * @returns {Object} Context value con config, loading, helpers y acciones
 */
export const useConfigEmpresa = () => {
  const context = useContext(ConfigEmpresaContext);
  if (!context) {
    throw new Error('useConfigEmpresa debe usarse dentro de ConfigEmpresaProvider');
  }
  return context;
};
